import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import {
    fetchTimer,
    startTimer,
    syncTimerAction,
    switchTimerMode,
    setMode,
    setTimeLeft,
    setIsRunning,
    setTotalTime,
    setStartTime,
    setElapsedOffset,
    clearError,
    setError,
    setTimerStatus,
    setTimer,
} from '../../redux/Slices/timerSlice';


import './scss/TimeTracker.scss';
import Play from '../icons/Play';
import Pause from '../icons/Pause';
import Stop from '../icons/Stop';
import RotateCw from '../icons/RotateCw';
import Timer from '../icons/Timer';
import Countdown from '../icons/Countdown';
import { useTimer } from '../../hooks/useTimer';
import { useBoard } from '../../context/BoardContext';

const ProgressCircle = ({ progress, mode, animate }) => {
    const radius = 60;
    const circumference = 2 * Math.PI * radius;
    const dashOffset = circumference - (progress / 100) * circumference;

    return (
        <svg width="140" height="140" className="progress-circle">
            <defs>
                <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#4f46e5" />
                    <stop offset="100%" stopColor="#06b6d4" />
                </linearGradient>
            </defs>
            <circle cx="70" cy="70" r={radius} stroke="#eee" strokeWidth="8" fill="none" />
            {mode === 'stopwatch' && (
                <g className={animate ? 'rotate-circle' : ''}>
                    <circle
                        cx="70"
                        cy="70"
                        r="60"
                        stroke="url(#progressGradient)"
                        strokeWidth="8.3"
                        fill="none"
                        strokeDasharray={circumference}
                        strokeLinecap="round"
                    />
                </g>
            )}
            {mode === 'countdown' && (
                <circle
                    cx="70"
                    cy="70"
                    r={radius}
                    stroke="url(#progressGradient)"
                    strokeWidth="8.3"
                    fill="none"
                    strokeDasharray={circumference}
                    strokeDashoffset={dashOffset}
                    strokeLinecap="round"
                />
            )}
        </svg>
    );
};

const TimeTracker = ({ cardId, cardTimer }) => {

    const dispatch = useDispatch();
    const { setBoard } = useBoard();
    const intervalRef = useRef(null);

    // Local inputs for countdown
    const [hours, setHours] = useState(0);
    const [minutes, setMinutes] = useState(30);
    const [seconds, setSeconds] = useState(0);

    const {
        mode,
        timeLeft,
        isRunning,
        totalTime,
        timerStatus,
        error,
        startTime,
        elapsedOffset,
        stopwatchElapsed,
        countdownElapsed,
        hydrated,
    } = useTimer(cardId);


    // --- Optimistic Redux hydration ---
    useEffect(() => {
        if (!hydrated && cardTimer?.startTime && ['running', 'paused'].includes(cardTimer?.status)) {
            dispatch(setTimer({ cardId, timerData: cardTimer }));
        }
    }, [cardId]);


    // --- Sync countdown inputs on mount ---
    useEffect(() => {
        if (mode === 'countdown' && timeLeft > 0 && !isRunning) {
            setHours(Math.floor(timeLeft / 3600));
            setMinutes(Math.floor((timeLeft % 3600) / 60));
            setSeconds(timeLeft % 60);
        }
    }, [mode, timeLeft, isRunning]);

    const formatTime = (sec) => {
        const h = String(Math.floor(sec / 3600)).padStart(2, '0');
        const m = String(Math.floor((sec % 3600) / 60)).padStart(2, '0');
        const s = String(sec % 60).padStart(2, '0');
        return `${h}:${m}:${s}`;
    };
    const handleBoardStatus = (status) => {
        // Update board state optimistically
        setBoard((prevBoard) => ({
            ...prevBoard,
            actionLists: prevBoard.actionLists.map((actionList) => ({
                ...actionList,
                cards: actionList.cards.map((c) =>
                    c.shortId === cardId
                        ? { ...c, timer: { ...c.timer, status } }
                        : c
                ),
            })),
        }));
    }
    const startTimerLoop = (startTimeVal, modeVal, duration, offset = 0) => {


        if (intervalRef.current) clearInterval(intervalRef.current);

        intervalRef.current = setInterval(() => {
            const now = Date.now();
            const sessionElapsed = Math.floor((now - startTimeVal) / 1000);

            if (modeVal === 'stopwatch') {
                const totalElapsed = offset + sessionElapsed;
                dispatch(setTimeLeft({ cardId, timeLeft: totalElapsed }));
            } else {
                const remaining = duration - (offset + sessionElapsed);
                if (remaining <= 0) {
                    clearInterval(intervalRef.current);
                    dispatch(setIsRunning({ cardId, isRunning: false }));
                    dispatch(setTimeLeft({ cardId, timeLeft: 0 }));
                    dispatch(syncTimerAction({ cardId, action: 'stop', mode: modeVal, duration, elapsed: 0 }));
                } else {
                    dispatch(setTimeLeft({ cardId, timeLeft: remaining }));
                }
            }
        }, 1000);
    };

    // Start timer loop if running
    useEffect(() => {
        if (isRunning && startTime != null) {
            if (mode === 'countdown') {
                startTimerLoop(startTime, mode, totalTime, countdownElapsed);
            }
            else {
                startTimerLoop(startTime, mode, totalTime, elapsedOffset);
            }
            // startTimerLoop(startTime, mode, totalTime, elapsedOffset);

        }
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [isRunning, startTime, mode, totalTime, elapsedOffset]);

    const handleModeSwitch = async (newMode) => {
        // Prevent mode switching when timer is running
        if (isRunning || timerStatus === 'running') {
            dispatch(setError({ cardId, error: 'Cannot change timer mode while running. Stop, pause, or reset the timer first.' }));
            return;
        }

        dispatch(clearError({ cardId }));

        try {
            // Use the backend endpoint for mode switching
            await dispatch(switchTimerMode({ cardId, mode: newMode })).unwrap();

            // Reset countdown inputs when switching to countdown mode
            if (newMode === 'countdown') {
                setHours(0);
                setMinutes(30);
                setSeconds(0);
            }
        } catch (err) {
            dispatch(setError({ cardId, error: err.message || 'Failed to switch timer mode' }));
            // Fallback to local mode switch if backend fails
            dispatch(setMode({ cardId, mode: newMode }));
        }
    };

    const handleStart = async () => {
        if (isRunning) return;
        dispatch(clearError({ cardId }));

        const duration = hours * 3600 + minutes * 60 + seconds;

        if (mode === 'countdown' && duration <= 0) {
            dispatch(setError({ cardId, error: 'Please set a valid duration for countdown' }));
            return;
        }

        dispatch(setIsRunning({ cardId, isRunning: true }));
        dispatch(setTimerStatus({ cardId, status: 'running' }));
        dispatch(setTotalTime({ cardId, totalTime: duration }));

        handleBoardStatus('running');

        if (mode === 'countdown') {
            dispatch(setTimeLeft({ cardId, timeLeft: duration }));
        }

        try {
            const elapsed =
                mode === 'countdown'
                    ? countdownElapsed
                    : stopwatchElapsed;

            const actionResult = await dispatch(syncTimerAction({ cardId, action: 'start', mode, duration, elapsed })).unwrap();

            const serverStartTime = typeof actionResult.startTime === 'string'
                ? new Date(actionResult.startTime).getTime()
                : actionResult.startTime;

            const serverDuration = actionResult.duration || 0;

            const serverElapsed = mode === 'countdown' ? actionResult.countdownElapsed : actionResult.stopwatchElapsed;

            if (Math.abs(serverStartTime - Date.now()) > 1000) {
                dispatch(setStartTime({ cardId, startTime: serverStartTime }));
                dispatch(setElapsedOffset({ cardId, elapsedOffset: serverElapsed }));
            } else {
                dispatch(setStartTime({ cardId, startTime: Date.now() }));
                dispatch(setElapsedOffset({ cardId, elapsedOffset: serverElapsed }));
            }
        } catch (err) {
            dispatch(setError({ cardId, error: err.message || 'Failed to start timer' }));
            dispatch(fetchTimer({ cardId }));
        }
    };

    const handleResume = async () => {
        if (isRunning) return;
        dispatch(clearError({ cardId }));

        const duration = hours * 3600 + minutes * 60 + seconds;

        if (mode === 'countdown' && duration <= 0) {
            dispatch(setError({ cardId, error: 'Please set a valid duration for countdown' }));
            return;
        }

        dispatch(setIsRunning({ cardId, isRunning: true }));
        dispatch(setTimerStatus({ cardId, status: 'running' }));
        dispatch(setTotalTime({ cardId, totalTime: duration }));

        handleBoardStatus('running');

        if (mode === 'countdown') {
            dispatch(setTimeLeft({ cardId, timeLeft: duration }));
        }

        try {

            const elapsed =
                mode === 'countdown'
                    ? countdownElapsed
                    : stopwatchElapsed;

            const actionResult = await dispatch(syncTimerAction({ cardId, action: 'resume', mode, duration, elapsed })).unwrap();

            const serverStartTime = new Date(actionResult.startTime).getTime();
            const serverElapsed =
                mode === 'countdown'
                    ? actionResult.countdownElapsed
                    : actionResult.stopwatchElapsed;

            if (Math.abs(serverStartTime - Date.now()) > 1000) {
                dispatch(setStartTime({ cardId, startTime: serverStartTime }));
                dispatch(setElapsedOffset({ cardId, elapsedOffset: serverElapsed }));
            } else {
                dispatch(setStartTime({ cardId, startTime: Date.now() }));
                dispatch(setElapsedOffset({ cardId, elapsedOffset: serverElapsed }));
            }
        } catch (err) {
            dispatch(setError({ cardId, error: err.message || 'Failed to resume timer' }));
            dispatch(fetchTimer({ cardId }));
        }
    };


    const handlePause = async () => {
        dispatch(setIsRunning({ cardId, isRunning: false }));
        dispatch(setTimerStatus({ cardId, status: 'paused' }));
        clearInterval(intervalRef.current);
        handleBoardStatus('paused');

        try {
            await dispatch(syncTimerAction({ cardId, action: 'pause', mode, duration: totalTime, elapsed: timeLeft }));
        } catch (err) {
            dispatch(setError({ cardId, error: err.message }));
        }
    };

    const handleStop = async () => {
        dispatch(setIsRunning({ cardId, isRunning: false }));
        dispatch(setTimerStatus({ cardId, status: 'stopped' }));
        dispatch(setTimeLeft({ cardId, timeLeft: 0 }));
        dispatch(setStartTime({ cardId, startTime: null }));
        dispatch(setElapsedOffset({ cardId, elapsedOffset: 0 }));
        dispatch(setTotalTime({ cardId, totalTime: 0 }));
        clearInterval(intervalRef.current);
        handleBoardStatus('stopped');

        try {
            const elapsed =
                mode === 'countdown'
                    ? countdownElapsed
                    : stopwatchElapsed;

            await dispatch(syncTimerAction({ cardId, action: 'stop', mode, duration: totalTime, elapsed }));

        } catch (err) {
            dispatch(setError({ cardId, error: err.message }));
        }
    };

    const handleReset = async () => {
        dispatch(setIsRunning({ cardId, isRunning: false }));
        dispatch(setTimerStatus({ cardId, status: 'idle' }));
        dispatch(setTimeLeft({ cardId, timeLeft: 0 }));
        dispatch(setStartTime({ cardId, startTime: null }));
        dispatch(setElapsedOffset({ cardId, elapsedOffset: 0 }));
        dispatch(setTotalTime({ cardId, totalTime: 0 }));
        clearInterval(intervalRef.current);
        handleBoardStatus('idle');

        try {
            await dispatch(syncTimerAction({ cardId, action: 'reset', mode, duration: totalTime, elapsed: timeLeft }));
            dispatch(fetchTimer({ cardId }));
        } catch (err) {
            dispatch(setError({ cardId, error: err.message }));
        }
    };

    const progress =
        mode === 'countdown' && totalTime > 0
            ? ((totalTime - timeLeft) / totalTime) * 100
            : mode === 'stopwatch'
                ? (stopwatchElapsed % 60) * (100 / 60) // if you want rotating loop every minute
                : 100;


    // 🧠 Protect against false countdown ending
    if (mode === 'countdown' && !hydrated && timeLeft === 0 && isRunning) {
        return null; // or a placeholder
    }



    return (

        <div className="time-tracker">
            {error && (
                <div className="timer-error">
                    {error}
                    <button onClick={() => dispatch(clearError({ cardId }))}>×</button>
                </div>
            )}
            <div className='timer-popover'>
                <h3>Timer</h3>
            </div>

            <div className="time-circle-wrapper">
                <ProgressCircle progress={progress} mode={mode} animate={mode === 'stopwatch' && isRunning} />
                <div className="time-display-centered">{formatTime(timeLeft)}</div>
            </div>

            <div className="mode-switch-container">
                <div className="mode-switch">
                    <button
                        className={mode === 'stopwatch' ? 'active' : ''}
                        data-tooltip-content={isRunning || timerStatus === 'running' ? "Cannot switch mode while timer is running" : "Stopwatch"}
                        data-tooltip-position="top"
                        data-popover-trigger
                        aria-label="Stopwatch"
                        onClick={() => handleModeSwitch('stopwatch')}
                        disabled={isRunning || timerStatus === 'running'}
                    >
                        <Timer size={18} />
                    </button>
                    <button
                        className={mode === 'countdown' ? 'active' : ''}
                        data-tooltip-content={isRunning || timerStatus === 'running' ? "Cannot switch mode while timer is running" : "Countdown"}
                        data-tooltip-position="top"
                        data-popover-trigger
                        aria-label="Countdown"
                        onClick={() => handleModeSwitch('countdown')}
                        disabled={isRunning || timerStatus === 'running'}
                    >
                        <Countdown size={18} />
                    </button>
                </div>
            </div>

            {mode === 'countdown' && !isRunning && (
                <div className="time-inputs">
                    <input
                        type="number"
                        min="0"
                        value={hours}
                        onChange={(e) => {
                            const value = Math.max(0, parseInt(e.target.value) || 0);
                            setHours(value);
                            setError(null);
                        }}
                        disabled={isRunning || timeLeft > 0}
                    />
                    <span>:</span>
                    <input
                        type="number"
                        min="0"
                        max="59"
                        value={minutes}
                        onChange={(e) => {
                            const value = Math.min(59, Math.max(0, parseInt(e.target.value) || 0));
                            setMinutes(value);
                            setError(null);
                        }}
                        disabled={isRunning || timeLeft > 0}
                    />
                    <span>:</span>
                    <input
                        type="number"
                        min="0"
                        max="59"
                        value={seconds}
                        onChange={(e) => {
                            const value = Math.min(59, Math.max(0, parseInt(e.target.value) || 0));
                            setSeconds(value);
                            setError(null);
                        }}
                        disabled={isRunning || timeLeft > 0}
                    />
                </div>
            )}

            <div className="controls">
                <button
                    onClick={handleStop}
                    data-tooltip-content="Stop"
                    data-tooltip-position="top"
                    data-popover-trigger
                    className="timer-stop"
                    aria-label="Stop"
                    disabled={timeLeft === 0}
                >
                    <Stop size={16} color='#fff' />
                </button>

                {!isRunning ? (
                    <button
                        onClick={timerStatus === 'paused' ? handleResume : handleStart}
                        className="timer-start"
                        disabled={timerStatus !== 'paused' && mode === 'countdown' && hours === 0 && minutes === 0 && seconds === 0}
                        style={{ backgroundColor: timerStatus === 'paused' ? '#10B981' : '#F59E0B' }}
                    >
                        <Play size={18} color="#fff" />
                        {timerStatus === 'paused' ? 'Resume' : 'Play'}
                    </button>
                ) : (
                    <button
                        onClick={handlePause}
                        className="timer-pause"
                        style={{ backgroundColor: '#10B981' }}
                    >
                        <Pause size={18} color="#fff" />
                        Pause
                    </button>
                )}


                <button
                    onClick={handleReset}
                    className="timer-reset"
                    data-tooltip-content="Reset"
                    data-tooltip-position="top"
                    data-popover-trigger
                    aria-label="Reset"
                // disabled={!isRunning && timeLeft === 0}
                >
                    <RotateCw size={18} />
                </button>
            </div>
        </div>
    );
};

export default TimeTracker;
